/**
 * Outcomes that mean the candidate is actually in play.
 *
 * Three jobs: the badge colour on a call row, whether the Track action is
 * offered on it, and what the Daily Calls Report leaves out. Deliberately one
 * set rather than three — a second list meaning "positive" would drift from
 * this one, and the drift would show up as a candidate the recruiter cannot
 * track from a call that says Interested.
 *
 * The near-misses are left out on purpose. `info_sent` and
 * `awaiting_confirmation` are waiting on the candidate, `call_back` and `hold`
 * are commitments, and `notice_period` / `salary_issue` / `location_issue` are
 * blocked. None of them is a decision to put somebody forward.
 *
 * This is an affordance, not a rule: the server accepts a submission from any
 * call, exactly as POST /submissions accepts one for any candidate. Hiding the
 * button is a nudge, and a recruiter who means it can still submit by hand.
 *
 * Fetched, not declared — the server filters on the same list, so a literal
 * here would be a second copy of a rule that has to agree. Lives in lib rather
 * than beside the table because two callers share it now: the table itself,
 * and the page that decides whether a newly logged call is trackable.
 * `staleTime: Infinity` and a shared query key mean the second caller costs
 * nothing.
 *
 * The fallback is the historical value, used only until the request lands: an
 * empty set would briefly hide every Track button, which reads as a bug.
 */
import { useQuery } from '@tanstack/react-query';
import { client } from '../api/client';
import type { CallOutcome } from '../types';

const POSITIVE_FALLBACK = new Set<CallOutcome>(['interested', 'shortlisted']);

export function usePositiveOutcomes(): Set<CallOutcome> {
  const { data } = useQuery<CallOutcome[]>({
    queryKey: ['call-outcomes', 'positive'],
    queryFn: async () =>
      (await client.get('/api/v1/calls/outcomes/positive')).data,
    staleTime: Infinity,
  });
  return data ? new Set(data) : POSITIVE_FALLBACK;
}
